import { useTranslation } from 'react-i18next';
import {
  formatNumber,
  pushEventToDataLayer,
  stripSpaces,
} from '../../../core/helpers';
import { CountryThreshold } from '../../../core/types';
import CountryLabel from '../../../components/CountryLabel/CountryLabel';
import classNames from 'classnames';
import useLinkToMainSite from '../../../hooks/useLinkToMainSite';
import { MouseEventHandler, useMemo } from 'react';
import ThresholdIndicator from './ThresholdIndicator';
import './SetupCheckThreshold.scss';
import SetupCheckQuestion from './SetupCheckQuestion';
import { ReactComponent as LinkIcon } from '../../../icons/link.svg';
import { useModal } from '../../../components/Modal/useModal';
import AdjustmentsModal from './AdjustmentsModal';
import { GTMEvents, PagesEnum } from '../../../core/constants';
import RetroactiveFilingModal from './RetroactiveFilingModal';
import Button from '../../../components/Button';

interface Props {
  threshold: number;
  thresholdByCountry: CountryThreshold[];
  className?: string;
  onAnswerOssQuestion: (v: boolean) => MouseEventHandler;
  questionText: string;
  hasOss: boolean | undefined;
  showQuestion: boolean;
  title: JSX.Element;
  crossedOssThresholdLabel?: string;
}

export default function SetupCheckThreshold({
  threshold,
  thresholdByCountry,
  className = '',
  onAnswerOssQuestion,
  questionText,
  hasOss,
  showQuestion,
  title,
  crossedOssThresholdLabel,
}: Props) {
  const {
    isModalOpened: isAdjustmentModalOpened,
    closeModal: closeAdjustmentModal,
    openModal: openAdjustmentModal,
  } = useModal();

  const {
    isModalOpened: isRetroactiveFilingModalOpened,
    closeModal: closeRetroactiveFilingModal,
    openModal: openRetroactiveFilingModal,
  } = useModal();

  const { t } = useTranslation();
  const { getLink } = useLinkToMainSite();

  const isAffected = threshold >= 9500;
  const otherCountriesThreshold = useMemo(
    () =>
      thresholdByCountry
        .slice(5)
        .reduce((acc, item) => acc + item.threshold.value, 0),
    [thresholdByCountry]
  );

  return (
    <div
      className={`SetupCheck__card d-flex flex-column justify-content-betweeen ${className}`}
    >
      <div className='px-3 px-lg-4'>
        <div className='row gx-lg-5'>
          <div className='col-12 col-lg-6 SetupCheckThreshold__left pt-3 py-lg-4 d-lg-flex'>
            <div className='mb-4 mb-lg-0 d-flex flex-column justify-content-between flex-grow-1'>
              <div>
                <div className='fw-800 mb-lg-2'>{t('EU-Wide threshold')}</div>
                <div className='SetupCheck__text mb-2'>
                  {t(
                    'Since the 1st of July 2021 a single EU-wide threshold replaces national thresholds. After reaching this threshold, seller have to register for OSS and provide a single filing once per quarter'
                  )}
                </div>
                <a
                  className='SetupCheck__link d-inline-block mb-lg-5'
                  href={getLink(PagesEnum.oss)}
                  target='_blank'
                  rel='noopener noreferrer'
                  onClick={() => {
                    pushEventToDataLayer(GTMEvents.setup_check_click_about_oss);
                  }}
                >
                  {t('About OSS')} ⟶
                </a>
              </div>
              {showQuestion && (
                <SetupCheckQuestion
                  answer={hasOss}
                  className='d-none d-lg-block mb-lg-1'
                  text={questionText}
                  onClickYes={onAnswerOssQuestion(true)}
                  onClickNo={onAnswerOssQuestion(false)}
                />
              )}
            </div>
            <div className='SetupCheck__separator mx-n3 d-lg-none' />
          </div>
          <div className='col-12 col-lg-6 pt-3 pt-lg-4 pb-4'>
            <div className='d-flex flex-column align-items-center'>
              {title}
              <div
                className={classNames('SetupCheck__threshold mb-2 mb-lg-0', {
                  'SetupCheck__threshold--affected': isAffected,
                })}
                data-testid='threshold'
              >
                ≈
                {stripSpaces(
                  formatNumber(threshold, {
                    currency: 'EUR',
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  })
                )}
              </div>
              <ThresholdIndicator />
            </div>
            <div className='fw-800 mt-4 mb-2'>{t('Top-selling countries')}</div>
            {thresholdByCountry.slice(0, 5).map((item, i) => (
              <div
                className='row row-cols-auto justify-content-between mb-2'
                key={i}
              >
                <div className='col'>
                  <CountryLabel country={item.country} />
                </div>
                <div className='col'>
                  ≈
                  {item.threshold?.value &&
                    stripSpaces(
                      formatNumber(Math.round(item.threshold?.value), {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                        currency: 'EUR',
                      })
                    )}
                </div>
              </div>
            ))}
            <div className='d-flex justify-content-between align-items-center mt-4 mb-2'>
              <div className='fw-800'>{t('Other countries')}</div>
              <div data-testid='other-countries-threshold'>
                ≈
                {stripSpaces(
                  formatNumber(Math.round(otherCountriesThreshold), {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                    currency: 'EUR',
                  })
                )}
              </div>
            </div>
            <div className='row gx-3 mb-n2 row-cols-auto'>
              {thresholdByCountry.slice(5).map((item, i) => (
                <div className='col mb-2' key={i}>
                  <CountryLabel country={item.country} />
                </div>
              ))}
            </div>
            {threshold > 11000 && crossedOssThresholdLabel && (
              <div className='mt-3'>
                <div className='fw-800 mb-2'>
                  {t('Since *quarter* required').replace(
                    '*quarter*',
                    crossedOssThresholdLabel
                  )}
                  :
                </div>
                <div className='row row-cols-lg-2 row-cols-1 mb-3'>
                  <div className='col mb-2 d-flex align-items-start'>
                    ✓&nbsp;<div>{t('Registration for OSS')}</div>
                  </div>
                  <div className='col mb-2 d-flex align-items-start'>
                    ✓&nbsp;
                    <Button
                      styling='naked-blue'
                      className='text-start'
                      onClick={() => {
                        openRetroactiveFilingModal();
                        pushEventToDataLayer(
                          GTMEvents.setup_check_click_retroactive_filing
                        );
                      }}
                    >
                      {t('Retroactive filing')}
                    </Button>
                  </div>
                  <div className='col mb-2 d-flex align-items-start'>
                    ✓&nbsp;
                    <Button
                      styling='naked-blue'
                      className='text-start'
                      onClick={() => {
                        pushEventToDataLayer(
                          GTMEvents.setup_check_click_invoice_adjustments
                        );
                        openAdjustmentModal();
                      }}
                    >
                      {t('Invoice adjustments')}
                    </Button>
                  </div>
                </div>
                <div>
                  <a
                    className='d-inline-block SetupCheckThreshold__link'
                    href='https://vat-one-stop-shop.ec.europa.eu/one-stop-shop/declare-and-pay-oss_en#paragraph_98'
                    target='_blank'
                    rel='noopener noreferrer'
                    onClick={() => {
                      pushEventToDataLayer(
                        GTMEvents.setup_check_click_penalties_link
                      );
                    }}
                  >
                    <span className='me-2'>
                      {t('Penalties are possible depending on the  situation')}
                    </span>
                    <LinkIcon className='mb-1' />
                  </a>
                </div>
              </div>
            )}
            {showQuestion && (
              <div className='d-lg-none'>
                <div className='SetupCheck__separator mx-n3 d-lg-none mt-4 mb-2' />
                <SetupCheckQuestion
                  answer={hasOss}
                  text={questionText}
                  onClickYes={onAnswerOssQuestion(true)}
                  onClickNo={onAnswerOssQuestion(false)}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      <AdjustmentsModal
        isModalOpened={isAdjustmentModalOpened}
        closeModal={closeAdjustmentModal}
      />
      <RetroactiveFilingModal
        closeModal={closeRetroactiveFilingModal}
        isModalOpened={isRetroactiveFilingModalOpened}
      />
    </div>
  );
}
